// ============================================================================
// Componente: FinanceiroFitaHistorico.js
// Objetivo: Mostrar o histórico de importações da Fita (quem importou e quando).
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { History, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';

const cardBox = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)' };

const formatarDataHora = (valor) => {
    if (!valor) return '—';
    const d = new Date(valor);
    if (isNaN(d.getTime())) return String(valor);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const FinanceiroFitaHistorico = ({ refreshKey }) => {
    const [historico, setHistorico] = useState([]);
    const [indisponivel, setIndisponivel] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    const token = localStorage.getItem('authToken');

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const res = await fetch('/api/fita/importacao/historico', { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Erro ${res.status}`); }
            const data = await res.json();
            setHistorico(data.historico || []);
            setIndisponivel(!!data.indisponivel);
        } catch (e) {
            setErro(e.message || 'Erro ao carregar o histórico.');
        } finally {
            setCarregando(false);
        }
    }, [token]);

    useEffect(() => { carregar(); }, [carregar, refreshKey]);

    const th = { padding: '10px 16px', textAlign: 'left', fontWeight: '800', color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' };
    const td = { padding: '10px 16px', color: '#334155', fontSize: '13px', borderBottom: '1px solid #f1f5f9' };

    return (
        <div style={cardBox}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                    <History size={18} color="#16a39c" /> Histórico de Importações
                </h4>
                <button
                    onClick={carregar}
                    disabled={carregando}
                    title="Atualizar"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '700', fontSize: '13px', cursor: carregando ? 'not-allowed' : 'pointer' }}
                >
                    <RefreshCw size={15} className={carregando ? 'animate-spin' : undefined} /> Atualizar
                </button>
            </div>

            {erro && (
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontWeight: '600', fontSize: '14px' }}>
                    {erro}
                </div>
            )}

            {!erro && indisponivel && (
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontWeight: '600', fontSize: '14px' }}>
                    O histórico ainda não está disponível — a tabela <code>dtbAtosImportacaoLog</code> precisa ser criada no banco. As próximas importações serão registradas assim que ela existir.
                </div>
            )}

            {!erro && !indisponivel && historico.length === 0 && !carregando && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>
                    Nenhuma importação registrada ainda.
                </div>
            )}

            {!erro && !indisponivel && historico.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: '10px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={th}>Data / Hora</th>
                                <th style={th}>Usuário</th>
                                <th style={th}>Tipo</th>
                                <th style={th}>Arquivo</th>
                                <th style={{ ...th, textAlign: 'center' }}>Status</th>
                                <th style={{ ...th, textAlign: 'right' }}>Inseridos</th>
                                <th style={{ ...th, textAlign: 'right' }}>Removidos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {historico.map((h, idx) => (
                                <tr key={h.id ?? idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: '600' }}>{formatarDataHora(h.dataImportacao)}</td>
                                    <td style={td}>{h.usuarioNome || '—'}</td>
                                    <td style={td}>{h.tipo}</td>
                                    <td style={{ ...td, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h.arquivo || ''}>{h.arquivo || '—'}</td>
                                    <td style={{ ...td, textAlign: 'center' }}>
                                        {h.status === 'sucesso' ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', backgroundColor: '#dcfce7', color: '#15803d' }}>
                                                <CheckCircle2 size={13} /> Sucesso
                                            </span>
                                        ) : (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                                                <AlertCircle size={13} /> Erro
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{h.registrosInseridos ?? '—'}</td>
                                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{h.registrosRemovidos ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default FinanceiroFitaHistorico;
